import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeRedirect } from "@relais/contracts";
import { supabase } from "../lib/supabase.js";
import { fetchRole, signOut, useSession } from "../lib/auth.js";
import { LanguageSwitcher } from "../components/LanguageSwitcher.js";

export const Route = createFileRoute("/login")({
  // `redirect` n'est déclaré que pour que le garde racine puisse l'écrire de
  // façon typée ; le nettoyage qui fait foi est celui du composant
  // (cf. sanitizeRedirect : la validation de route ne filtre pas).
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" ? { redirect: search.redirect } : {},
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Destination d'origine, posée par le garde racine quand la session a
  // expiré sur un lien profond.
  const redirect = sanitizeRedirect(Route.useSearch().redirect);
  const session = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Session déjà valide (onglet resté ouvert, retour arrière vers /login) :
  // on repart aussitôt. `href` plutôt que `to` : la destination est une chaîne
  // arbitraire validée à l'entrée, pas une route typée. Le rôle est revérifié
  // par le garde de __root.tsx, donc pas de court-circuit ici.
  useEffect(() => {
    if (session.status !== "authed") return;
    navigate({ href: redirect ?? "/", replace: true });
  }, [session.status, redirect, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) {
      setLoading(false);
      setError(signInError.message);
      return;
    }

    // Garde de rôle (mirror admin-front, commit c0a8ebe) : Poseurs est réservé
    // au rôle `poseur`. Un conducteur/admin authentifié est immédiatement
    // déconnecté — les contrôles de rôle côté API/service (TasksService,
    // etc.) bloquent déjà ses actions, on l'éjecte aussi de l'UI au plus
    // tôt, avant même d'atteindre le dashboard.
    try {
      const role = await fetchRole();
      if (role !== "poseur") {
        await signOut();
        setLoading(false);
        setError(t("auth.login.notAuthorized"));
        return;
      }
    } catch {
      await signOut();
      setLoading(false);
      setError(t("auth.login.genericError"));
      return;
    }

    setLoading(false);
    navigate({ href: redirect ?? "/", replace: true });
  }

  return (
    <div className="max-w-sm mx-auto mt-8 space-y-6">
      <div className="flex justify-end">
        <LanguageSwitcher />
      </div>
      <h2 className="text-xl font-semibold">{t("auth.login.title")}</h2>
      {redirect && (
        <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {t("auth.login.sessionExpired")}
        </p>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="text-sm text-muted-foreground">
            {t("auth.login.emailLabel")}
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2 bg-background"
            required
          />
        </label>
        <label className="block">
          <span className="text-sm text-muted-foreground">
            {t("auth.login.passwordLabel")}
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2 bg-background"
            required
          />
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-primary text-primary-foreground py-2 disabled:opacity-50"
        >
          {loading ? t("auth.login.submitting") : t("auth.login.submit")}
        </button>
      </form>
    </div>
  );
}
